import { useMemo } from "react";
import { useGetMapDevicesQuery, useGetLocationsQuery } from "features/icccApiSlice";
import useDashboardData from "../../components/useDashboardData";
import { DEFAULT_POLL_MS, HEALTH } from "../../model/constants";

export default function useMapData() {
  const {
    healthByAsset,
    isLoading: healthLoading,
    isFetching: healthFetching,
    refetch: refetchHealth,
    lastUpdated,
  } = useDashboardData({ pollMs: DEFAULT_POLL_MS });

  const devicesQ = useGetMapDevicesQuery(undefined, { pollingInterval: DEFAULT_POLL_MS });
  const locationsQ = useGetLocationsQuery(undefined, { pollingInterval: DEFAULT_POLL_MS });

  // Marker colour must match the ICCC dashboard legend — alert-derived health,
  // not the raw severity normalizeDevice carries (see model/constants.js G7).
  const devices = useMemo(
    () =>
      (devicesQ.data || []).map((d) => ({
        ...d,
        health: healthByAsset[d.id] || HEALTH.HEALTHY,
      })),
    [devicesQ.data, healthByAsset]
  );

  const locations = useMemo(() => {
    const withMaps = (locationsQ.data || []).filter((l) => l.backgroundImageURL);
    return [...withMaps].sort((a, b) => {
      const byParent = (a.parentLocationName || "").localeCompare(b.parentLocationName || "");
      return byParent !== 0 ? byParent : (a.name || "").localeCompare(b.name || "");
    });
  }, [locationsQ.data]);

  return {
    devices,
    locations,
    isLoading: devicesQ.isLoading || locationsQ.isLoading || healthLoading,
    isFetching: devicesQ.isFetching || locationsQ.isFetching || healthFetching,
    isError: devicesQ.isError || locationsQ.isError,
    refetch: () => {
      devicesQ.refetch();
      locationsQ.refetch();
      refetchHealth();
    },
    lastUpdated,
  };
}
